import * as Sentry from '@sentry/react';

const API_BASE_URL = 'https://api.bookingkit.de/v3';
const LOCAL_STORAGE_TOKEN_KEY = 'bookingKitToken';
const MAX_SECONDS_BEFORE_EXPIRY_FOR_TOKEN_RENEWAL = 300;

// Some restricted browsers (in-app webviews, privacy modes, sandboxed
// iframes) either have `window.localStorage === null` or throw
// SecurityError on access. We still want the booking flow to work there
// — just without caching — so every localStorage call is wrapped in
// try/catch. We also only report the underlying error to Sentry once per
// session to avoid flooding when every call fails.
let localStorageErrorReported = false;

function reportLocalStorageError(error: unknown, op: 'get' | 'set' | 'remove'): void {
  if (localStorageErrorReported) return;
  localStorageErrorReported = true;
  Sentry.captureException(error, { tags: { localStorageOp: op } });
}

function safeGetItem(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    reportLocalStorageError(error, 'get');
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    reportLocalStorageError(error, 'set');
  }
}

function safeRemoveItem(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    reportLocalStorageError(error, 'remove');
  }
}

export interface BookingKitPrice {
  title: string;
  currency: string;
  value: string;
}

export interface BookingKitDate {
  id: string;
  date: string;
  available_slots: number;
}

export interface EventDetails {
  id: string;
  name: string;
  prices: BookingKitPrice[];
}

export type EventDetailsWithDates = EventDetails & {
  dates: Array<BookingKitDate>;
};

interface TokenData {
  token: string;
  expiryTime: number;
}

interface ApiToken {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

function getTokenDataFromLocalStorage(): TokenData | null {
  const tokenDataStr = safeGetItem(LOCAL_STORAGE_TOKEN_KEY);
  if (!tokenDataStr) return null;
  try {
    const tokenData = JSON.parse(tokenDataStr);
    if (
      tokenData === null ||
      typeof tokenData !== 'object' ||
      typeof tokenData.token !== 'string' ||
      typeof tokenData.expiryTime !== 'number'
    ) {
      throw new Error('Found tokenData with bad structure in localStorage: ' + tokenDataStr);
    }
    return tokenData as TokenData;
  } catch (error) {
    safeRemoveItem(LOCAL_STORAGE_TOKEN_KEY);
    console.error(error);
    Sentry.captureException(error);
    return null;
  }
}

export class BookingKitClient {
  private tokenData: TokenData | null = null;
  private tokenPromise: Promise<ApiToken> | null = null;

  private async getApiToken(): Promise<ApiToken> {
    if (!this.tokenPromise) {
      this.tokenPromise = this.fetchApiToken().catch(err => {
        // Clear the cached promise so the next call retries instead of
        // returning the same rejected promise forever
        this.tokenPromise = null;
        throw err;
      });
    }
    return this.tokenPromise;
  }

  private async fetchApiToken(): Promise<ApiToken> {
    const maxRetries = 3;
    const retryDelayMs = 1000;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const resp = await fetch('/.netlify/functions/get-bookingkit-token', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (resp.status === 200) {
        const apiToken: ApiToken = await resp.json();
        this.tokenPromise = null;
        return apiToken;
      }

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs * attempt));
      } else {
        throw new Error(`Got response with status code ${resp.status} for get-bookingkit-token`);
      }
    }

    throw new Error('Unexpected end of retry loop');
  }

  private async makeRequest(endpoint: string, params: Record<string, string> = {}) {
    // If tokenData not initialized, try to get it from localStorage
    if (!this.tokenData) {
      this.tokenData = getTokenDataFromLocalStorage();
    }
    // If tokenData expiryTime is close, clear tokenData including localStorage
    if (
      this.tokenData &&
      Date.now() + MAX_SECONDS_BEFORE_EXPIRY_FOR_TOKEN_RENEWAL * 1000 > this.tokenData.expiryTime
    ) {
      this.tokenData = null;
      safeRemoveItem(LOCAL_STORAGE_TOKEN_KEY);
    }
    // If we don't have a valid tokenData, get one from the api
    if (!this.tokenData) {
      const apiToken = await this.getApiToken();
      this.tokenData = {
        token: apiToken.access_token,
        expiryTime: Date.now() + apiToken.expires_in * 1000,
      };
      safeSetItem(LOCAL_STORAGE_TOKEN_KEY, JSON.stringify(this.tokenData));
    }

    const url = new URL(`${API_BASE_URL}${endpoint}`);
    for (const key of Object.keys(params)) {
      url.searchParams.append(key, params[key]);
    }
    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${this.tokenData.token}`,
      },
    });

    if (!response.ok) {
      console.error({ endpoint, params });
      throw new Error(`Error making request: ${response.statusText}`);
    }

    return response.json();
  }

  async getEventDetails(eventId: string, options: { withDates?: false }): Promise<EventDetails>;

  async getEventDetails(
    eventId: string,
    options: { withDates: true },
  ): Promise<EventDetailsWithDates>;

  async getEventDetails(
    eventId: string,
    options: { withDates?: boolean } = {},
  ): Promise<EventDetails | EventDetailsWithDates> {
    const { withDates = false } = options;
    if (withDates) {
      // TODO Fetch only needed params
      const [event, eventDates] = await Promise.all([
        this.makeRequest(`/events/${eventId}`) as Promise<EventDetails>,
        this.makeRequest(`/events/${eventId}/dates`) as Promise<Array<BookingKitDate>>,
      ]);
      return {
        ...event,
        dates: eventDates,
      };
    } else {
      // TODO Fetch only needed params
      return this.makeRequest(`/events/${eventId}`) as Promise<EventDetails>;
    }
  }
}

export default new BookingKitClient();
